import React from 'react';

import { Box, Typography } from '@material-ui/core';
import { Controller, FormProvider } from 'react-hook-form';

import { PageLoading } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { AppSwitch } from '@vyce/core/src/components/inputs';
import { SettingsCard, SettingsPage, SettingsSectionConfig } from '@vyce/core/src/components/SettingsCard';

import { ModuleItem } from './components';
import { useTeamSettingsData } from './hooks';
import { payrollOptions, timeAttendanceOptions } from './config';
import { useStyles } from './styles';
import { Props } from './types';

export const TeamsSettings = (props: Props) => {
  const classes = useStyles();
  const {
    mainLoading,
    buttonLoading,
    methods,
    activate,
    isPaySync,
    isTimeSync,
    isShowButton,
    paySchedules,
    sites,
    updateTeamsSettings,
  } = useTeamSettingsData(props);
  const [openCards, setOpenCards] = React.useState<Record<string, boolean>>({});

  const { control } = methods;

  const isOpen = (key: string) => !!openCards[key];
  const setOpen = (key: string) => (expanded: boolean) =>
    setOpenCards(prev => ({ ...prev, [key]: expanded }));

  if (mainLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <PageLoading />
      </Box>
    );
  }

  const sections: SettingsSectionConfig[] = [
    {
      label: 'Mirroring',
      rows: [
        {
          cards: [
            {
              key: 'mirror',
              search: 'mirror users other modules time attendance payroll automatic',
              node: (
                <SettingsCard
                  title="Mirror users from other Vyce modules"
                  description="Keep this team's member list up to date on its own, instead of adding and removing people by hand. Pick the module to follow below."
                  status={activate ? 'On' : 'Off'}
                  statusTone={activate ? 'on' : 'off'}
                  expanded={isOpen('mirror')}
                  onExpandedChange={setOpen('mirror')}
                  headerControl={
                    <Controller
                      control={control}
                      name="active"
                      render={({ field }) => (
                        <AppSwitch
                          {...field}
                          checked={!!field.value}
                          inputProps={{ 'aria-label': 'Mirror users from other Vyce modules' }}
                        />
                      )}
                    />
                  }
                >
                  <Typography className={classes.hintText}>
                    Mirroring keeps your Teams data current by managing members from one of your other
                    modules. Follow Time &amp; Attendance and the team tracks who is actually clocking in;
                    follow Payroll and it tracks who is actually being paid.
                  </Typography>

                  <Typography className={classes.hintText}>
                    People are added as soon as they appear in the module you follow, and removed once they
                    have been quiet for the period you set.
                  </Typography>
                </SettingsCard>
              ),
            },
          ],
        },
        // The per-module cards only make sense once mirroring itself is on.
        ...(activate
          ? [
              {
                paired: true,
                cards: [
                  {
                    key: 'time',
                    search: 'mirror time attendance locations clock in weeks',
                    node: (
                      <ModuleItem
                        isSelected={isTimeSync}
                        type="time"
                        options={timeAttendanceOptions}
                        name="recent_time_periods"
                        items={sites}
                        selectName="time_sites"
                        expanded={isOpen('time')}
                        onExpandedChange={setOpen('time')}
                      />
                    ),
                  },
                  {
                    key: 'pay',
                    search: 'mirror payroll pay schedules paid periods',
                    node: (
                      <ModuleItem
                        isSelected={isPaySync}
                        type="pay"
                        options={payrollOptions}
                        name="recent_pay_periods"
                        items={paySchedules}
                        selectName="pay_schedules"
                        expanded={isOpen('pay')}
                        onExpandedChange={setOpen('pay')}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ],
    },
  ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(updateTeamsSettings)}>
        <SettingsPage
          title="Sync settings"
          description="How this team keeps its member list up to date from your other Vyce modules."
          sections={sections}
          footer={
            isShowButton ? (
              <Box className={classes.footer}>
                <AppButton type="submit" variant="contained" color="primary" loading={buttonLoading}>
                  Save changes
                </AppButton>
              </Box>
            ) : undefined
          }
        />
      </form>
    </FormProvider>
  );
};
